import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Linking,
  Platform,
  Alert,
  ActivityIndicator,
  Modal,
  useWindowDimensions,
} from 'react-native';
import Share from 'react-native-share';
import { useAuth } from '../context/AuthContext';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { studentAPI } from '../services/api';
import { WebPageShell } from '../components/common/WebPageShell';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { EditStudentProfileModal } from '../components/profile/EditStudentProfileModal';
import { SectionEditModal, SectionEditField } from '../components/profile/SectionEditModal';
import { EditEmergencyContactModal } from '../components/profile/EditEmergencyContactModal';
import { EditAdditionalInfoModal } from '../components/profile/EditAdditionalInfoModal';
import { StudentDocumentTiles } from '../components/profile/StudentDocumentTiles';
import { Toast } from '../components/common/Toast';
import { formatLocalDateOnly } from '../utils/dateUtils';
import { uploadProfilePhoto } from '../utils/profilePhotoUpload';

type NavigationProp = NativeStackNavigationProp<any>;

export function ProfileScreen() {
  const { selectedStudent, refreshSelectedStudent } = useAuth();
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;
  const isSmallMobile = winWidth < 480;
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [previewingId, setPreviewingId] = React.useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = React.useState<{ url: string; contentType: string; name: string } | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = React.useState(false);
  const [showPersonalEdit, setShowPersonalEdit] = React.useState(false);
  const [showContactEdit, setShowContactEdit] = React.useState(false);
  const [showEmergencyEdit, setShowEmergencyEdit] = React.useState(false);
  const [showAdditionalInfoEdit, setShowAdditionalInfoEdit] = React.useState(false);
  const [showMedicalInfoEdit, setShowMedicalInfoEdit] = React.useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = React.useState(false);
  const { displayPhoto, refetch: refetchPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [showPhotoToast, setShowPhotoToast] = React.useState(false);
  const [photoToastMessage, setPhotoToastMessage] = React.useState('');
  const [photoToastType, setPhotoToastType] = React.useState<'success' | 'error'>('success');

  const handlePickPhoto = React.useCallback(() => {
    if (!selectedStudent?.id) return;
    const docObj = (globalThis as any).document;
    if (!docObj?.createElement) return;

    const input = docObj.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file: File | undefined = e.target.files?.[0];
      if (!file) return;

      setUploadingPhoto(true);

      const result = await uploadProfilePhoto(selectedStudent.id, {
        name: file.name,
        size: file.size,
        value: file,
      });
      setUploadingPhoto(false);

      if (result.ok) {
        refetchPhoto();
        // The same call filed the Passport Size Photo document, so the Documents
        // list below needs the refreshed student too.
        await refreshSelectedStudent();
        setPhotoToastType('success');
        setPhotoToastMessage('Photo updated successfully.');
      } else {
        setPhotoToastType('error');
        setPhotoToastMessage(result.message);
      }
      setShowPhotoToast(true);
    };
    input.click();
  }, [selectedStudent, refetchPhoto, refreshSelectedStudent]);

  if (!selectedStudent) {
    return (
      <View style={styles.container}>
        <Text>No student selected</Text>
      </View>
    );
  }

  // Safely extract from rawSnapshot or fallback to flattened Student
  const raw = selectedStudent.rawSnapshot || {};
  const profile = raw.Profile || raw.profile || {};
  const enrollments = raw.Enrollments || raw.enrollments || [];
  // A relative row is never deleted when its emergency/financial flag is
  // cleared (e.g. replaced by someone else) - only deactivated - so without
  // this filter every standalone "OTHER" contact ever entered stays listed
  // here forever, even once nothing about it is current.
  const relatives = (raw.Relatives || raw.relatives || []).filter((r: any) => (r.IsActive ?? r.isActive) !== false);
  const documents = raw.Documents || raw.documents || [];
  const questions = raw.Questions || raw.questions || [];
  const applicationId = profile.ApplicationId || profile.applicationId || '';

  const isMedicalQuestion = (q: any) => {
    const text = (q.QuestionText || q.questionText || '').toLowerCase();
    return text.includes('medical') || text.includes('medic') || text.includes('allerg') ||
      text.includes('disorder') || text.includes('behavioral') || text.includes('food') ||
      text.includes('suffer') || text.includes('condition');
  };

  const activeQuestions = questions.filter((q: any) => (q.AnswerYesNo ?? q.answerYesNo) !== null || (q.AnswerText ?? q.answerText));
  const medicalQuestions = activeQuestions.filter(isMedicalQuestion);
  const additionalQuestions = activeQuestions.filter((q: any) => !isMedicalQuestion(q));

  const allMedicalQuestions = questions.filter(isMedicalQuestion);
  const allAdditionalQuestions = questions.filter((q: any) => !isMedicalQuestion(q));

  const activeEnrollment = enrollments.find((e: any) => e.IsActive ?? e.isActive) || enrollments[0] || {};

  // Formatter helpers
  const formatQuestionText = (text: string) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const renderInfoRow = (label: string, value?: string | null, isLast = false) => (
    <React.Fragment key={label}>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue} numberOfLines={2}>{value || '-'}</Text>
      </View>
      {!isLast && <View style={styles.divider} />}
    </React.Fragment>
  );

  // --- Section-wise edit: Personal details ---
  const personalDetailsFields: SectionEditField[] = [
    { key: 'dateOfBirth', label: 'Date of birth', type: 'date' },
    { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'] },
    { key: 'birthPlace', label: 'Birth Place', type: 'text' },
    { key: 'nationality', label: 'Nationality', type: 'text' },
    { key: 'religion', label: 'Religion', type: 'text' },
    { key: 'religiousAffiliation', label: 'Religious Affiliation', type: 'text' },
    { key: 'isBaptized', label: 'Baptized', type: 'select', options: ['Yes', 'No'] },
    { key: 'isReligiousFinancialSupport', label: 'Relig. Financial Support', type: 'select', options: ['Yes', 'No'] },
  ];

  const personalDetailsInitialValues: Record<string, string> = {
    dateOfBirth: (profile.DateOfBirth || profile.dateOfBirth || '').toString().split('T')[0] || '',
    gender: profile.Gender || profile.gender || '',
    birthPlace: profile.BirthPlace || profile.birthPlace || '',
    nationality: profile.Nationality || profile.nationality || '',
    religion: profile.Religion || profile.religion || '',
    religiousAffiliation: profile.ReligiousAffiliation || profile.religiousAffiliation || '',
    isBaptized: (profile.IsBaptized ?? profile.isBaptized) ? 'Yes' : 'No',
    isReligiousFinancialSupport: (profile.IsReligiousFinancialSupport ?? profile.isReligiousFinancialSupport) ? 'Yes' : 'No',
  };

  const handleSavePersonalDetails = async (values: Record<string, string>) => {
    const payload = {
      dateOfBirth: values.dateOfBirth || null,
      gender: values.gender || null,
      birthPlace: values.birthPlace || null,
      nationality: values.nationality || null,
      religion: values.religion || null,
      religiousAffiliation: values.religiousAffiliation || null,
      isBaptized: values.isBaptized === 'Yes',
      isReligiousFinancialSupport: values.isReligiousFinancialSupport === 'Yes',
    };
    await studentAPI.updateStudentProfile(selectedStudent.id, payload);
    await refreshSelectedStudent();
    setShowPersonalEdit(false);
  };

  // --- Section-wise edit: Contact information ---
  const contactInfoFields: SectionEditField[] = [
    { key: 'studentEmail', label: 'Email', type: 'email' },
    { key: 'studentPhone', label: 'Phone', type: 'phone' },
    { key: 'visaStatus', label: 'Visa Status', type: 'text' },
    { key: 'address1', label: 'Address 1', type: 'text' },
    { key: 'poBox', label: 'PO Box', type: 'text' },
    { key: 'district', label: 'District', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'country', label: 'Country', type: 'text' },
    { key: 'pincode', label: 'Pincode', type: 'text' },
  ];

  const contactInfoInitialValues: Record<string, string> = {
    studentEmail: profile.StudentEmail || profile.studentEmail || '',
    studentPhone: profile.StudentPhone || profile.studentPhone || '',
    visaStatus: profile.VisaStatus || profile.visaStatus || '',
    address1: profile.Address1 || profile.address1 || selectedStudent.address1 || '',
    poBox: profile.POBox || profile.poBox || '',
    district: profile.District || profile.district || '',
    state: profile.State || profile.state || '',
    country: profile.Country || profile.country || '',
    pincode: profile.Pincode || profile.pincode || '',
  };

  const handleSaveContactInfo = async (values: Record<string, string>) => {
    const payload = {
      studentEmail: values.studentEmail || null,
      studentPhone: values.studentPhone || null,
      visaStatus: values.visaStatus || null,
      address1: values.address1 || null,
      poBox: values.poBox || null,
      district: values.district || null,
      state: values.state || null,
      country: values.country || null,
      pincode: values.pincode || null,
    };
    await studentAPI.updateStudentProfile(selectedStudent.id, payload);
    await refreshSelectedStudent();
    setShowContactEdit(false);
  };

  // --- Section-wise edit: Family & Contacts (Emergency contact) ---
  const handleSaveEmergencyContact = async (contactId: string, phone: string) => {
    const currentEmergency = relatives.find((r: any) => r.IsEmergencyContact ?? r.isEmergencyContact);
    const selectedRelative = relatives.find((r: any) => (r.Id || r.id) === contactId);
    if (!selectedRelative) return;

    const mapRelativeForUpdate = (rel: any, overrides: Record<string, any>) => ({
      id: rel.Id || rel.id,
      firstName: rel.FirstName || rel.firstName || '',
      lastName: rel.LastName || rel.lastName || '',
      middleName: rel.MiddleName || rel.middleName || null,
      relationCode: rel.RelationCode || rel.relationCode || rel.RelationName || rel.relationName || 'Guardian',
      phone: rel.Phone || rel.phone || null,
      cellPhone: rel.CellPhone || rel.cellPhone || null,
      email: rel.Email || rel.email || null,
      address: rel.Address || rel.address || null,
      poBox: rel.POBox || rel.poBox || null,
      workPhone: rel.WorkPhone || rel.workPhone || null,
      occupation: rel.Occupation || rel.occupation || null,
      nationality: rel.Nationality || rel.nationality || null,
      religion: rel.Religion || rel.religion || null,
      isEmergencyContact: rel.IsEmergencyContact ?? rel.isEmergencyContact ?? false,
      isBillingContact: rel.IsBillingContact ?? rel.isBillingContact ?? false,
      isInSameSchool: rel.IsInSameSchool ?? rel.isInSameSchool ?? false,
      isActive: true,
      ...overrides,
    });

    const updatedRelatives: any[] = [];

    if (currentEmergency && (currentEmergency.Id || currentEmergency.id) !== contactId) {
      updatedRelatives.push(mapRelativeForUpdate(currentEmergency, { isEmergencyContact: false }));
    }

    updatedRelatives.push(mapRelativeForUpdate(selectedRelative, {
      phone,
      cellPhone: phone,
      isEmergencyContact: true,
    }));

    await studentAPI.updateStudentProfile(selectedStudent.id, { relatives: updatedRelatives });
    await refreshSelectedStudent();
    setShowEmergencyEdit(false);
  };

  // --- Section-wise edit: Additional Information (Questions) ---
  const mergeQuestions = (updatedQuestions: any[]) => {
    const unchangedQuestions = questions.filter((q: any) => !updatedQuestions.some(uq => uq.questionCode === (q.QuestionCode || q.questionCode)));
    return [
      ...updatedQuestions,
      ...unchangedQuestions.map((q: any) => ({
        questionCode: q.QuestionCode || q.questionCode,
        answerYesNo: q.AnswerYesNo ?? q.answerYesNo,
        answerText: q.AnswerText || q.answerText || null,
        isActive: q.IsActive ?? q.isActive ?? true,
      }))
    ];
  };

  const handleSaveAdditionalInfo = async (questionResponses: any[]) => {
    await studentAPI.updateStudentProfile(selectedStudent.id, { questionResponses: mergeQuestions(questionResponses) });
    await refreshSelectedStudent();
    setShowAdditionalInfoEdit(false);
  };

  const handleSaveMedicalInfo = async (questionResponses: any[]) => {
    await studentAPI.updateStudentProfile(selectedStudent.id, { questionResponses: mergeQuestions(questionResponses) });
    await refreshSelectedStudent();
    setShowMedicalInfoEdit(false);
  };

  const fetchDocumentBlob = async (doc: any) => {
    const docId = doc.Id || doc.id;
    if (!docId || !selectedStudent.id) return null;
    const response = await studentAPI.getStudentDocumentBlob(selectedStudent.id, docId);
    const contentType = (response.headers?.['content-type'] as string) || 'application/octet-stream';
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType } as any);
    const docName = doc.DocumentName || doc.documentName || 'Document';
    return { blob, contentType, docName };
  };

  const handlePreviewDocument = async (doc: any) => {
    const docId = doc.Id || doc.id;
    if (!docId) return;

    try {
      setPreviewingId(docId);
      const result = await fetchDocumentBlob(doc);
      if (!result) return;
      const { blob, contentType, docName } = result;
      const url = URL.createObjectURL(blob);

      if (Platform.OS === 'web') {
        if (contentType.startsWith('image/')) {
          setPreviewDoc({ url, contentType, name: docName });
        } else {
          // PDFs and other browser-openable types: let the browser render its native viewer.
          (globalThis as any).window.open(url, '_blank');
        }
      } else {
        setPreviewDoc({ url, contentType, name: docName });
      }
    } catch (err) {
      console.error('Failed to preview document:', err);
      Alert.alert('Preview Failed', 'Unable to preview the document at this time.');
    } finally {
      setPreviewingId(null);
    }
  };

  const handleClosePreview = () => {
    if (previewDoc?.url) {
      URL.revokeObjectURL(previewDoc.url);
    }
    setPreviewDoc(null);
  };

  const handleDownloadDocument = async (doc: any) => {
    const docId = doc.Id || doc.id;
    if (!docId || !selectedStudent.id) return;

    try {
      setDownloadingId(docId);
      const result = await fetchDocumentBlob(doc);
      if (!result) return;
      const { blob, docName } = result;
      const filename = docName.replace(/\s+/g, '_');

      if (Platform.OS === 'web') {
        const url = URL.createObjectURL(blob);
        const link = (globalThis as any).document.createElement('a');
        link.href = url;
        link.download = filename;
        (globalThis as any).document.body.appendChild(link);
        link.click();
        (globalThis as any).document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64data = reader.result;
          if (base64data) {
            await Share.open({
              url: base64data as string,
              title: docName,
              filename: filename
            });
          }
        };
      }
    } catch (err) {
      console.error("Failed to download document:", err);
      Alert.alert('Download Failed', 'Unable to fetch the document at this time.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <WebPageShell title="Student Profile" maxWidth={1000}>
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.container, styles.content, isMobile && styles.contentMobile]}>
          {/* Toolbar */}


          {/* Student Card */}
          <View style={styles.studentCard}>
            <View style={styles.studentInfoRow}>
              <View style={styles.avatarWrapper}>
                {displayPhoto ? (
                  <Image source={{ uri: displayPhoto }} style={styles.avatarImage as any} />
                ) : (
                  <Text style={styles.avatarPlaceholder}>{selectedStudent.name.charAt(0).toUpperCase()}</Text>
                )}

                {uploadingPhoto && (
                  <View style={styles.avatarUploadOverlay}>
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  </View>
                )}

                <TouchableOpacity
                  style={styles.avatarEditBadge}
                  onPress={handlePickPhoto}
                  disabled={uploadingPhoto}
                >
                  <FeatherIcon name="camera" size={12} color="#1E40AF" />
                </TouchableOpacity>
              </View>
              <View style={styles.studentTextInfo}>
                <Text style={styles.studentName}>{selectedStudent.name}</Text>
                <View style={styles.badgeColumn}>
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>
                      Grade {activeEnrollment.ClassDisplayName || activeEnrollment.classDisplayName || selectedStudent.class}
                    </Text>
                  </View>
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>
                      ID {profile.StudentIdentificationNumber || profile.studentIdentificationNumber || selectedStudent.studentIdentificationNumber || '-'}
                    </Text>
                  </View>
                </View>
              </View>
              <TouchableOpacity style={{ padding: 8, alignSelf: 'flex-start' }} onPress={() => setIsEditModalVisible(true)}>
                <FeatherIcon name="edit-2" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>

            <TouchableOpacity style={styles.digitalIdBtn} onPress={() => navigation.navigate('StudentId' as any)}>
              <FeatherIcon name="credit-card" size={16} color={colors.primary} style={{ marginRight: 8 }} />
              <Text style={styles.digitalIdText}>View digital ID card</Text>
            </TouchableOpacity>
          </View>

          {/* Personal Details & Contact Information - Side by Side */}
          <View style={[styles.twoColumnRow, isMobile && styles.twoColumnRowMobile]}>
            {/* Personal details */}
            <View style={[styles.section, styles.flex1, isMobile && { marginBottom: 24 }]}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Personal details</Text>
                <TouchableOpacity style={styles.sectionEditBtn} onPress={() => setShowPersonalEdit(true)}>
                  <FeatherIcon name="edit-2" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
              <View style={styles.infoCard}>
                {renderInfoRow('Date of birth', formatLocalDateOnly(profile.DateOfBirth || profile.dateOfBirth, '-'))}
                {renderInfoRow('Gender', profile.Gender || profile.gender)}
                {renderInfoRow('Birth Place', profile.BirthPlace || profile.birthPlace)}
                {renderInfoRow('Nationality', profile.Nationality || profile.nationality)}
                {renderInfoRow('Religion', profile.Religion || profile.religion)}
                {renderInfoRow('Religious Affiliation', profile.ReligiousAffiliation || profile.religiousAffiliation)}
                {renderInfoRow('Baptized', (profile.IsBaptized ?? profile.isBaptized) ? 'Yes' : 'No')}
                {renderInfoRow('Relig. Financial Support', (profile.IsReligiousFinancialSupport ?? profile.isReligiousFinancialSupport) ? 'Yes' : 'No', true)}
              </View>
            </View>

            {/* Contact Information */}
            <View style={[styles.section, styles.flex1]}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Contact Information</Text>
                <TouchableOpacity style={styles.sectionEditBtn} onPress={() => setShowContactEdit(true)}>
                  <FeatherIcon name="edit-2" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
              <View style={styles.infoCard}>
                {renderInfoRow('Email', profile.StudentEmail || profile.studentEmail)}
                {renderInfoRow('Phone', profile.StudentPhone || profile.studentPhone)}
                {renderInfoRow('Visa Status', profile.VisaStatus || profile.visaStatus)}

                {renderInfoRow('Address 1', profile.Address1 || profile.address1 || selectedStudent.address1)}
                {renderInfoRow('PO Box', profile.POBox || profile.poBox)}
                {renderInfoRow('District', profile.District || profile.district)}
                {renderInfoRow('State', profile.State || profile.state)}
                {renderInfoRow('Country', profile.Country || profile.country)}
                {renderInfoRow('Pincode', profile.Pincode || profile.pincode, true)}
              </View>
            </View>
          </View>

          {/* Previous School Info */}
          {(profile.PreviousInstitution || profile.previousInstitution || profile.PreviousInstitutionContact || profile.previousInstitutionContact || profile.PreviousInstitutionAddress || profile.previousInstitutionAddress) ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Previous School Info</Text>
              <View style={styles.infoCard}>
                {[
                  { label: 'Prev. Institution', value: profile.PreviousInstitution || profile.previousInstitution },
                  { label: 'Prev. Inst. Contact', value: profile.PreviousInstitutionContact || profile.previousInstitutionContact },
                  { label: 'Prev. Inst. Address', value: profile.PreviousInstitutionAddress || profile.previousInstitutionAddress }
                ]
                  .filter(item => !!item.value)
                  .map((item, index, arr) => renderInfoRow(item.label, item.value, index === arr.length - 1))}
              </View>
            </View>
          ) : null}

          {/* Enrollments */}
          {enrollments.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Enrollment Info</Text>
              {enrollments.map((env: any, idx: number) => {
                const isLast = idx === enrollments.length - 1;
                return (
                  <View key={env.Id || env.id || idx} style={[styles.infoCard, !isLast && { marginBottom: 12 }]}>
                    {renderInfoRow('Academic Year', (env.AcademicYear || env.academicYear)?.toString())}
                    {renderInfoRow('Class', env.ClassDisplayName || env.classDisplayName)}
                    {renderInfoRow('Status', env.StatusCode || env.statusCode)}
                    {renderInfoRow('Payment Freq.', env.PaymentFrequency || env.paymentFrequency, true)}
                  </View>
                );
              })}
            </View>
          )}

          {/* Relatives */}
          {relatives.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Family & Contacts</Text>
                <TouchableOpacity style={styles.sectionEditBtn} onPress={() => setShowEmergencyEdit(true)}>
                  <FeatherIcon name="edit-2" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
              {relatives.map((rel: any, idx: number) => {
                const isLast = idx === relatives.length - 1;
                const name = `${rel.FirstName || rel.firstName || ''} ${rel.LastName || rel.lastName || ''}`.trim();
                const isEmergency = rel.IsEmergencyContact || rel.isEmergencyContact;
                const isFinancial = rel.IsBillingContact || rel.isBillingContact;
                const relationLabel = rel.RelationName || rel.relationName;
                // A standalone contact (not mother/father/guardian) is stored
                // with relationCode 'OTHER' since it has no real family
                // relation - show what they actually are instead of the raw
                // backend code.
                const displayRelation = String(relationLabel || '').toUpperCase() === 'OTHER'
                  ? [isEmergency && 'Emergency Contact', isFinancial && 'Financial Contact'].filter(Boolean).join(' & ') || 'Other'
                  : relationLabel;
                return (
                  <View key={rel.Id || rel.id || idx} style={[styles.relativeCard, !isLast && { marginBottom: 12 }]}>
                    <View style={styles.relativeHeader}>
                      <Text style={styles.relativeName}>{name}</Text>
                      <View style={styles.relativeBadgeRow}>
                        {isEmergency && (
                          <View style={styles.emergencyBadge}>
                            <Text style={styles.emergencyText}>Emergency</Text>
                          </View>
                        )}
                        {isFinancial && (
                          <View style={styles.financialBadge}>
                            <Text style={styles.financialText}>Financial</Text>
                          </View>
                        )}
                      </View>
                    </View>
                    <Text style={styles.relativeRelation}>{displayRelation}</Text>

                    <View style={styles.relativeContactRow}>
                      <FeatherIcon name="phone" size={14} color={colors.textSecondary} />
                      <Text style={styles.relativeContactText}>{rel.Phone || rel.phone || rel.CellPhone || rel.cellPhone || '-'}</Text>
                    </View>

                    <View style={styles.relativeContactRow}>
                      <FeatherIcon name="mail" size={14} color={colors.textSecondary} />
                      <Text style={styles.relativeContactText}>{rel.Email || rel.email || '-'}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* Medical Information (Questions) */}
          {medicalQuestions.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Medical Information</Text>
                <TouchableOpacity style={styles.sectionEditBtn} onPress={() => setShowMedicalInfoEdit(true)}>
                  <FeatherIcon name="edit-2" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
              <View style={styles.infoCard}>
                {medicalQuestions
                  .map((q: any, idx: number, arr: any[]) => {
                    const isLast = idx === arr.length - 1;
                    const isYesNo = (q.ResponseType || q.responseType) === 'YesNo';

                    let answerText = q.AnswerText || q.answerText || '';
                    if (isYesNo) {
                      const yesNo = q.AnswerYesNo ?? q.answerYesNo;
                      const yesNoStr = yesNo === true ? 'Yes' : (yesNo === false ? 'No' : '-');
                      const details = q.AnswerText || q.answerText;
                      if (yesNo === true && details) {
                        answerText = `${yesNoStr} - ${details}`;
                      } else {
                        answerText = yesNoStr;
                      }
                    }

                    return (
                      <View key={q.QuestionId || q.questionId || idx} style={[styles.questionItem, !isLast && styles.questionBorder]}>
                        <Text style={styles.questionText}>{formatQuestionText(q.QuestionText || q.questionText)}</Text>
                        <Text style={styles.answerText}>{answerText || '-'}</Text>
                      </View>
                    );
                  })}
              </View>
            </View>
          )}

          {/* Additional Information (Questions) */}
          {additionalQuestions.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Additional Information</Text>
                <TouchableOpacity style={styles.sectionEditBtn} onPress={() => setShowAdditionalInfoEdit(true)}>
                  <FeatherIcon name="edit-2" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
              <View style={styles.infoCard}>
                {additionalQuestions
                  .map((q: any, idx: number, arr: any[]) => {
                    const isLast = idx === arr.length - 1;
                    const isYesNo = (q.ResponseType || q.responseType) === 'YesNo';

                    let answerText = q.AnswerText || q.answerText || '';
                    if (isYesNo) {
                      const yesNo = q.AnswerYesNo ?? q.answerYesNo;
                      const yesNoStr = yesNo === true ? 'Yes' : (yesNo === false ? 'No' : '-');
                      const details = q.AnswerText || q.answerText;
                      if (yesNo === true && details) {
                        answerText = `${yesNoStr} - ${details}`;
                      } else {
                        answerText = yesNoStr;
                      }
                    }

                    return (
                      <View key={q.QuestionId || q.questionId || idx} style={[styles.questionItem, !isLast && styles.questionBorder]}>
                        <Text style={styles.questionText}>{formatQuestionText(q.QuestionText || q.questionText)}</Text>
                        <Text style={styles.answerText}>{answerText || '-'}</Text>
                      </View>
                    );
                  })}
              </View>
            </View>
          )}

          {/* Documents: one tile per document the school asks of an enrolled
              student; each uploads/replaces in place and previews once there. */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Documents</Text>
            <StudentDocumentTiles
              studentProfileId={selectedStudent.id}
              documents={documents}
              onPreview={handlePreviewDocument}
              onDownload={handleDownloadDocument}
              previewingId={previewingId}
              downloadingId={downloadingId}
              onUploaded={async () => { await refreshSelectedStudent(); }}
              onProfilePhotoUpdated={refetchPhoto}
            />
          </View>

          {/* Quick links */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Quick links</Text>
            <View style={[styles.quickLinksGrid, isSmallMobile && styles.quickLinksGridSmallMobile]}>

              <TouchableOpacity style={[styles.quickLinkBox, isSmallMobile && styles.quickLinkBoxSmallMobile]} onPress={() => navigation.navigate('Attendance' as any, { backTo: 'Student Profile' } as any)}>
                <View style={[styles.quickLinkIcon, { backgroundColor: '#FEF3C7' }]}>
                  <FeatherIcon name="calendar" size={18} color="#D97706" />
                </View>
                <Text style={styles.quickLinkLabel}>Attendance</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.quickLinkBox, isSmallMobile && styles.quickLinkBoxSmallMobile]} onPress={() => navigation.navigate('Leaves' as any, { backTo: 'Student Profile' } as any)}>
                <View style={[styles.quickLinkIcon, { backgroundColor: '#DCFCE7' }]}>
                  <FeatherIcon name="file-text" size={18} color="#16A34A" />
                </View>
                <Text style={styles.quickLinkLabel}>Leaves</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.quickLinkBox, isSmallMobile && styles.quickLinkBoxSmallMobile]} onPress={() => navigation.navigate('Academics' as any, { backTo: 'Student Profile' } as any)}>
                <View style={[styles.quickLinkIcon, { backgroundColor: colors.primaryLight }]}>
                  <FeatherIcon name="book" size={18} color={colors.primary} />
                </View>
                <Text style={styles.quickLinkLabel}>Academics</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.quickLinkBox, isSmallMobile && styles.quickLinkBoxSmallMobile]} onPress={() => navigation.navigate('Fees' as any, { backTo: 'Student Profile' } as any)}>
                <View style={[styles.quickLinkIcon, { backgroundColor: colors.primaryLight }]}>
                  <FeatherIcon name="credit-card" size={18} color={colors.primary} />
                </View>
                <Text style={styles.quickLinkLabel}>Fees</Text>
              </TouchableOpacity>
              {/* Transport hidden until the backend has a real transport module.
              <TouchableOpacity style={styles.quickLinkBox} onPress={() => navigation.navigate('Transport' as any)}>
                <View style={[styles.quickLinkIcon, { backgroundColor: '#FFEDD5' }]}>
                  <FeatherIcon name="truck" size={18} color="#EA580C" />
                </View>
                <Text style={styles.quickLinkLabel}>Transport</Text>
              </TouchableOpacity> */}
            </View>
          </View>

          <View style={{ height: 40 }} />
        </View>
      </SafeAreaView>

      <EditStudentProfileModal
        visible={isEditModalVisible}
        onClose={() => setIsEditModalVisible(false)}
        studentData={selectedStudent}
        onSuccess={async () => {
          // Was just closing the modal without refetching — the edited fields (including
          // Family & Contacts) kept showing stale data until the next full app reload.
          await refreshSelectedStudent();
          setIsEditModalVisible(false);
        }}
      />

      <SectionEditModal
        visible={showPersonalEdit}
        title="Edit Personal Details"
        fields={personalDetailsFields}
        initialValues={personalDetailsInitialValues}
        onClose={() => setShowPersonalEdit(false)}
        onSubmit={handleSavePersonalDetails}
      />

      <SectionEditModal
        visible={showContactEdit}
        title="Edit Contact Information"
        fields={contactInfoFields}
        initialValues={contactInfoInitialValues}
        onClose={() => setShowContactEdit(false)}
        onSubmit={handleSaveContactInfo}
      />

      <EditEmergencyContactModal
        visible={showEmergencyEdit}
        relatives={relatives}
        onClose={() => setShowEmergencyEdit(false)}
        onSubmit={handleSaveEmergencyContact}
      />

      <EditAdditionalInfoModal
        visible={showAdditionalInfoEdit}
        questions={allAdditionalQuestions}
        onClose={() => setShowAdditionalInfoEdit(false)}
        onSubmit={handleSaveAdditionalInfo}
      />

      <EditAdditionalInfoModal
        visible={showMedicalInfoEdit}
        questions={allMedicalQuestions}
        onClose={() => setShowMedicalInfoEdit(false)}
        onSubmit={handleSaveMedicalInfo}
      />

      <Modal visible={!!previewDoc} transparent animationType="fade" onRequestClose={handleClosePreview}>
        <View style={styles.previewOverlay}>
          <TouchableOpacity style={styles.previewCloseBtn} onPress={handleClosePreview}>
            <FeatherIcon name="x" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          {previewDoc && (
            <Image source={{ uri: previewDoc.url }} style={styles.previewImage} resizeMode="contain" />
          )}
        </View>
      </Modal>

      <Toast
        visible={showPhotoToast}
        message={photoToastMessage}
        type={photoToastType}
        onDismiss={() => setShowPhotoToast(false)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  safeArea: {
    flexGrow: 1,
    backgroundColor: colors.background,
  },
  container: {
    flexGrow: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  contentMobile: {
    paddingHorizontal: 0,
  },
  screenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerRightActions: {
    flexDirection: 'row',
    gap: 12,
  },
  screenTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  studentCard: {
    // Preserved as the original literal navy in light mode — it must not shift just
    // because dark mode now shares a "primary" token with other, lighter blue accents.
    backgroundColor: isDark ? colors.primary : '#1E40AF',
    borderRadius: 24,
    padding: 20,
    marginBottom: 24,
    shadowColor: isDark ? colors.primary : '#1E40AF',
    shadowOpacity: 0.2,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 5,
  },
  studentInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  avatarWrapper: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    position: 'relative',
  } as any,
  avatarPlaceholder: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.primary,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  avatarUploadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
  } as any,
  avatarEditBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#1E40AF',
  } as any,
  studentTextInfo: {
    flex: 1,
  },
  studentName: {
    fontSize: 20,
    fontWeight: '800',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  badgeColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  badge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 6,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  digitalIdBtn: {
    backgroundColor: colors.card,
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 14,
  },
  digitalIdText: {
    color: colors.primary,
    fontWeight: '700',
    fontSize: 15,
  },
  section: {
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
  } as any,
  twoColumnRow: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 24,
  } as any,
  twoColumnRowMobile: {
    flexDirection: 'column',
    gap: 0,
  } as any,
  flex1: {
    flex: 1,
    marginBottom: 0,
    justifyContent: 'flex-start',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionEditBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 8,
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    flex: 1,
  } as any,
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 14,
  },
  infoLabel: {
    fontSize: 14,
    color: colors.textMuted,
    fontWeight: '500',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '700',
    flex: 1.5,
    textAlign: 'right',
  },
  divider: {
    height: 1,
    backgroundColor: colors.divider,
  },
  relativeCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 16,
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  relativeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  relativeName: {
    fontSize: 16,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  relativeBadgeRow: {
    flexDirection: 'row',
    gap: 6,
  },
  emergencyBadge: {
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  emergencyText: {
    color: '#EF4444',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  financialBadge: {
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  financialText: {
    color: '#16A34A',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  relativeR: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: 4,
  },
  relativeRelation: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 12,
  },
  relativeContactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  } as any,
  relativeContactText: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  documentsContainer: {
    gap: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
  } as any,
  documentCard: {
    flex: 1,
    minWidth: 280,
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  } as any,
  uploadPromptCard: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  previewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewCloseBtn: {
    position: 'absolute',
    top: 24,
    right: 24,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  previewImage: {
    width: '90%',
    height: '80%',
  },
  docIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  docInfo: {
    flex: 1,
  },
  docActionBtn: {
    padding: 4,
    marginLeft: 8,
  },
  docName: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 2,
  },
  docStatus: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '600',
  },
  quickLinksGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 12,
  } as any,
  quickLinksGridSmallMobile: {
    rowGap: 10,
  } as any,
  quickLinkBox: {
    width: '48%',
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  } as any,
  quickLinkBoxSmallMobile: {
    width: '100%',
    padding: 14,
  } as any,
  quickLinkIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quickLinkLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  questionItem: {
    paddingVertical: 14,
  },
  questionBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  questionText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '600',
    marginBottom: 6,
    lineHeight: 18,
  },
  answerText: {
    fontSize: 15,
    color: colors.textPrimary,
    fontWeight: '700',
    lineHeight: 22,
  }
});
